import * as _ from 'lodash';

import {
  type HistoryRecord,
  type ValidHistoryKey,
} from '../componentOutliers/types';
import {type BucketedLine} from '../types';
import {BIN_POINT_TO_USE} from './../../../util/plotHelpers/chartTypes';
import {type BucketedQueryState} from './bucketedQueryManager';

/**
 * `run` with each history wrapped in the nearest `loFiRun` points outside its
 * range, or `run` itself when there are none. Neither line is changed: both
 * belong to a cache that delivers them again.
 */
export function bracketHistoryData(
  run: BucketedLine,
  loFiRun: BucketedLine,
  xAxisKey?: string
): BucketedLine {
  let bracketed = false;
  const history = run.history.map((h, index) => {
    const matchedLoFiHistory = loFiRun.history[index];
    const lastKey = `${xAxisKey}${BIN_POINT_TO_USE}` as ValidHistoryKey;

    if (h.length < 2 || !matchedLoFiHistory || !matchedLoFiHistory.length) {
      return h;
    }

    const [minX, maxX] = [h[0][lastKey], h[h.length - 1][lastKey]];

    // Find closest points in lower fidelity data that bracket the high fidelity range
    const leftBracketIndex = _.sortedIndexBy(
      matchedLoFiHistory,
      {[lastKey]: minX} as unknown as HistoryRecord, // we only need a subset of the history record value to sort
      lastKey
    );

    const leftBracket =
      leftBracketIndex > 0 ? matchedLoFiHistory[leftBracketIndex - 1] : null;

    const rightBracketIndex = _.sortedIndexBy(
      matchedLoFiHistory,
      {[lastKey]: maxX} as unknown as HistoryRecord, // we only need a subset of the history record value to sort
      lastKey
    );

    const rightBracket =
      rightBracketIndex < matchedLoFiHistory.length - 1
        ? matchedLoFiHistory[rightBracketIndex + 1]
        : null;

    if (!leftBracket && !rightBracket) {
      return h;
    }
    bracketed = true;
    // concat copies `h` in one step. A spread after the first element copies
    // it one point at a time, about three times slower.
    const left = leftBracket ? [leftBracket] : [];
    return left.concat(h, rightBracket ? [rightBracket] : []);
  });
  return bracketed ? {...run, history} : run;
}

/**
 * The last bracketed line made from each cached line. Cached lines are never
 * changed, so a line delivered again can reuse its bracketed line.
 */
const bracketedLines = new WeakMap<
  BucketedLine,
  {line: BucketedLine; loFiRun: BucketedLine; xAxisKey: string}
>();

/**
 * `bracketHistoryData`, returning the same line object each time the same
 * line is delivered. The reducer skips lines that are the same object; a new
 * copy would make it compare every point.
 */
function bracketCachedLine(
  run: BucketedLine,
  loFiRun: BucketedLine,
  xAxisKey: string
): BucketedLine {
  const cached = bracketedLines.get(run);
  if (cached?.loFiRun === loFiRun && cached.xAxisKey === xAxisKey) {
    return cached.line;
  }
  const line = bracketHistoryData(run, loFiRun, xAxisKey);
  bracketedLines.set(run, {line, loFiRun, xAxisKey});
  return line;
}

/**
 * The intent here is to wrap the histories returned by the API with points outside the specified range if some can be found in the previously queried histories. Imagine a metric logged every 1000 steps, if you hit the API with a zoom range like 2250 - 222250 the first and last points on the returned data will be logged at 2300 and 222000 respectively. This will cause the chart to look like the lines do not extend past the zoomed range. Wrapping these with two points outside the range lets react vis plot the line to the edges of the chart.
 *
 * It's okay to swallow errors here as the charts are still usable without this functionality, so if there's something unexpected here we can just sniff it out in Datadog and fix it as needed.
 *
 * Returns a new state and leaves `state` unchanged. Its lines are the ones the
 * manager or store caches: they are delivered again when a panel
 * resubscribes, and unchanged runs carry over into the next fetch's delta
 * baseline, so bracketing them in place added more points with every
 * delivery.
 *
 * Perf notes: w/ 50 runs and 1000 points per run this takes < .5ms
 */
export function bracketHistoriesData(
  state: BucketedQueryState,
  xAxisKey?: string
): BucketedQueryState {
  // no-op if there is no lower fidelity data to bracket with
  if (!state.lowerFidelityData || !xAxisKey || !state.data) {
    return state;
  }

  let bracketedAny = false;
  const runsById: typeof state.data.runsById = {};
  Object.entries(state.data.runsById).forEach(([runId, run]) => {
    runsById[runId] = run;
    const lowerFidelityMatch = state.lowerFidelityData?.runsById[runId];
    if (!lowerFidelityMatch) {
      return;
    }
    try {
      runsById[runId] = bracketCachedLine(run, lowerFidelityMatch, xAxisKey);
      bracketedAny ||= runsById[runId] !== run;
    } catch (e) {
      console.error('bracketHistoriesData', e);
      return;
    }
  });

  return bracketedAny ? {...state, data: {...state.data, runsById}} : state;
}

export function mapEnabledByRunSets(
  runSets: {
    id: string;
    enabled?: boolean;
  }[]
) {
  return runSets.reduce(
    (acc, val) => {
      acc[val.id!] = val.enabled ?? false;
      return acc;
    },
    {} as Record<string, boolean>
  );
}
